import { ChangeEvent, useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  TextField,
  MenuItem,
  Select,
  Button,
  FormControlLabel,
  Box,
  Tab,
  Tabs,
  Checkbox,
  CardMedia,
  IconButton,
  Input,
  SelectChangeEvent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { 
  useGetFlavourListQuery,
  useGetToppingListQuery,
  useUploadFileMutation,
} from "../../store/api/commonAPI";
import { 
  useGetCategoryListQuery,
  useGetProductListQuery,
  useCreateStoreMutation,
  useUpdateStoreMutation,
  useGetStoreDetailQuery,
} from "../../store/api/manageAPI";
import { showToast, showError } from "../../constants/toasts";
import Loader from "../../constants/Loader";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

type DayOfWeek =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";

interface Availability {
  enabled: boolean;
  start: string;
  end: string;
}

const AddStore = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const storeId = (location.state as any)?.storeId;
  const isEditMode = !!storeId;

  const [valueTabs, setValueTabs] = useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  // Form states
  const [image, setImage] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [storeName, setStoreName] = useState<string>("");
  const [storeCode, setStoreCode] = useState<string>("");
  const [flavorIds, setFlavorIds] = useState<string[]>([]);
  const [toppingIds, setToppingIds] = useState<string[]>([]);
  const [productCategories, setProductCategories] = useState<string[]>([]);
  const [productsByCategory, setProductsByCategory] = useState<Record<string, string[]>>({});
  
  // Address fields
  const [fullAddress, setFullAddress] = useState<string>("");
  const [street, setStreet] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [zipCode, setZipCode] = useState<string>("");
  const [region, setRegion] = useState<string>("");

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Helper function to clear error
  const clearError = (fieldName: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  // API hooks
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const [createStore, { isLoading: isCreating }] = useCreateStoreMutation();
  const [updateStore, { isLoading: isUpdating }] = useUpdateStoreMutation();
  const isLoading = isUploading || isCreating || isUpdating;

  // Fetch store details for edit mode
  const { data: storeDetailData, isLoading: isLoadingStoreDetail } = useGetStoreDetailQuery(
    { id: storeId },
    { skip: !isEditMode }
  );

  const storeData = storeDetailData?.data?.data?.[0];
  const storeCategories = storeDetailData?.data?.storeCategory || [];
  const storeProducts = storeDetailData?.data?.storeProduct || [];
  const storeFlavors = storeDetailData?.data?.storeFlavor || [];
  const storeToppings = storeDetailData?.data?.storeTopping || [];

  // Fetch dropdown data
  const { data: flavorData } = useGetFlavourListQuery({ page: 1, limit: 1000 });
  const { data: toppingData } = useGetToppingListQuery({ page: 1, limit: 1000 });
  const { data: categoriesData } = useGetCategoryListQuery({ page: 1, limit: 1000 });
  
  // Extract lists
  const flavorList = useMemo(() => {
    if (flavorData?.data?.flavour && Array.isArray(flavorData.data.flavour)) {
      return flavorData.data.flavour.map((item: any) => ({
        id: item._id || item.id,
        name: item.name || "N/A",
      }));
    }
    return [];
  }, [flavorData]);

  const toppingList = useMemo(() => {
    if (toppingData?.data?.topping && Array.isArray(toppingData.data.topping)) {
      return toppingData.data.topping.map((item: any) => ({
        id: item._id || item.id,
        name: item.name || "N/A",
      }));
    }
    return [];
  }, [toppingData]);

  const categoryList = useMemo(() => {
    if (categoriesData?.data?.list && Array.isArray(categoriesData.data.list)) {
      return categoriesData.data.list.map((item: any) => ({
        id: item._id || item.id,
        name: item.name || "N/A",
      }));
    }
    return [];
  }, [categoriesData]);

  // Fetch products for selected categories
  const { data: productsData } = useGetProductListQuery({
    page: 1,
    limit: 1000,
    search: "",
  });

  const productList = useMemo(() => {
    if (productsData?.data?.product && Array.isArray(productsData.data.product)) {
      return productsData.data.product.map((item: any) => ({
        id: item._id || item.id,
        name: item.name || "N/A",
        categoryId: item.categoryDetails?._id || item.categoryId,
      }));
    }
    return [];
  }, [productsData]);

  // Get products for a specific category
  const getProductsForCategory = (categoryId: string) => {
    return productList.filter((product: any) => product.categoryId === categoryId);
  };

  // Convert ISO time to HH:MM format
  const convertISOToTime = (isoString?: string): string => {
    if (!isoString) return "09:00";
    try {
      const date = new Date(isoString);
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    } catch {
      return "09:00";
    }
  };

  const [availability, setAvailability] = useState<Record<DayOfWeek, Availability>>({
    Monday: { enabled: true, start: "09:00", end: "18:00" },
    Tuesday: { enabled: true, start: "09:00", end: "18:00" },
    Wednesday: { enabled: true, start: "09:00", end: "18:00" },
    Thursday: { enabled: true, start: "09:00", end: "18:00" },
    Friday: { enabled: true, start: "09:00", end: "18:00" },
    Saturday: { enabled: false, start: "09:00", end: "18:00" },
    Sunday: { enabled: false, start: "09:00", end: "18:00" },
  });

  // Populate form when store data is fetched (edit mode)
  useEffect(() => {
    if (isEditMode && storeData) {
      // Basic info
      setStoreName(storeData.name || "");
      setStoreCode(storeData.code || "");
      
      // Image
      if (storeData.image) {
        setImage(storeData.image);
        setUploadedImageUrl(storeData.image);
      }

      // Address
      if (storeData.address) {
        setFullAddress(storeData.address.fullAddress || "");
        setStreet(storeData.address.street || "");
        setCity(storeData.address.city || "");
        setState(storeData.address.state || "");
        setCountry(storeData.address.country || "");
        setZipCode(storeData.address.zipCode || "");
      }

      // Region
      setRegion(storeData.region || "");

      // Flavors
      if (storeFlavors.length > 0) {
        const flavorIds = storeFlavors
          .map((flavor: any) => flavor.flavorDetails?._id)
          .filter(Boolean);
        setFlavorIds(flavorIds);
      }

      // Toppings
      if (storeToppings.length > 0) {
        const toppingIds = storeToppings
          .map((topping: any) => topping.toppingDetails?._id)
          .filter(Boolean);
        setToppingIds(toppingIds);
      }

      // Categories and Products
      if (storeCategories.length > 0) {
        const categoryIds = storeCategories
          .map((cat: any) => cat.categoryDetails?._id)
          .filter(Boolean);
        setProductCategories(categoryIds);

        // Map products by category - get product IDs from storeProducts
        const productsByCat: Record<string, string[]> = {};
        const allProductIds = storeProducts
          .map((prod: any) => prod.productDetails?._id)
          .filter(Boolean);

        // For each category, find products that belong to it
        storeCategories.forEach((cat: any) => {
          const categoryId = cat.categoryDetails?._id;
          if (categoryId) {
            // Find products that belong to this category from productList
            const categoryProducts = productList
              .filter((prod: any) => prod.categoryId === categoryId && allProductIds.includes(prod.id))
              .map((prod: any) => prod.id)
              .filter(Boolean);
            productsByCat[categoryId] = categoryProducts;
          }
        });
        setProductsByCategory(productsByCat);
      }

      // Availability
      if (storeData.storeAvailability) {
        const weekdayMap: Record<string, DayOfWeek> = {
          weekday1: "Monday",
          weekday2: "Tuesday",
          weekday3: "Wednesday",
          weekday4: "Thursday",
          weekday5: "Friday",
          weekday6: "Saturday",
          weekday7: "Sunday",
        };

        const newAvailability: Record<DayOfWeek, Availability> = {
          Monday: { enabled: false, start: "09:00", end: "18:00" },
          Tuesday: { enabled: false, start: "09:00", end: "18:00" },
          Wednesday: { enabled: false, start: "09:00", end: "18:00" },
          Thursday: { enabled: false, start: "09:00", end: "18:00" },
          Friday: { enabled: false, start: "09:00", end: "18:00" },
          Saturday: { enabled: false, start: "09:00", end: "18:00" },
          Sunday: { enabled: false, start: "09:00", end: "18:00" },
        };

        Object.keys(weekdayMap).forEach((key) => {
          const day = weekdayMap[key];
          const dayData = storeData.storeAvailability?.[key as keyof typeof storeData.storeAvailability];
          if (dayData) {
            newAvailability[day] = {
              enabled: dayData.isActive || false,
              start: convertISOToTime(dayData.startTime),
              end: convertISOToTime(dayData.endTime),
            };
          }
        });

        setAvailability(newAvailability);
      }
    }
  }, [isEditMode, storeData, storeFlavors, storeToppings, storeCategories, storeProducts]);

  const toggleAvailability = (day: DayOfWeek) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: { ...prev[day], enabled: !prev[day].enabled },
    }));
  };

  const updateTime = (day: DayOfWeek, field: "start" | "end", value: string) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  // Convert time to ISO format for API
  const convertTimeToISO = (time: string): string => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const date = new Date();
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    return date.toISOString();
  };

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!storeName.trim()) {
      newErrors.storeName = "Store name is required";
    }

    if (!storeCode.trim()) {
      newErrors.storeCode = "Store code is required";
    }

    if (!isEditMode && !image && !uploadedImageUrl) {
      newErrors.image = "Store image is required";
    }
    if (isEditMode && !image && !uploadedImageUrl && !storeData?.image) {
      newErrors.image = "Store image is required";
    }

    if (!fullAddress.trim()) {
      newErrors.fullAddress = "Full address is required";
    }

    if (!street.trim()) {
      newErrors.street = "Street is required";
    }

    if (!city.trim()) {
      newErrors.city = "City is required";
    }

    if (!state.trim()) {
      newErrors.state = "State is required";
    }

    if (!country.trim()) {
      newErrors.country = "Country is required";
    }

    if (!zipCode.trim()) {
      newErrors.zipCode = "Zip code is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      showError("Please fill in all required fields");
      return;
    }

    try {
      let imageUrl = uploadedImageUrl;

      // Upload image if a new file is selected
      if (imageFile) {
        const uploadResponse = await uploadFile({ file: imageFile }).unwrap();
        const uploadedUrl = typeof uploadResponse.data === 'string' 
          ? uploadResponse.data 
          : uploadResponse.data?.url || uploadResponse.data?.fileUrl || "";
        imageUrl = uploadedUrl;
        if (!imageUrl) {
          showError("Failed to upload image");
          return;
        }
      }

      if (!imageUrl && !isEditMode) {
        showError("Store image is required");
        return;
      }
      // In edit mode, use existing image if no new image uploaded
      if (isEditMode && !imageUrl) {
        imageUrl = storeData?.image || "";
      }

      // Prepare weekday data
      const weekdayMap: Record<string, DayOfWeek> = {
        weekday1: "Monday",
        weekday2: "Tuesday",
        weekday3: "Wednesday",
        weekday4: "Thursday",
        weekday5: "Friday",
        weekday6: "Saturday",
        weekday7: "Sunday",
      };

      const weekdayData: Record<string, any> = {};
      Object.keys(weekdayMap).forEach((key, index) => {
        const day = weekdayMap[key];
        const dayData = availability[day];
        if (dayData.enabled) {
          weekdayData[key] = {
            isActive: true,
            startTime: convertTimeToISO(dayData.start),
            endTime: convertTimeToISO(dayData.end),
          };
        } else {
          weekdayData[key] = {
            isActive: false,
          };
        }
      });

      // Prepare categories data
      const categoriesData = productCategories.map((categoryId) => ({
        categoryId,
        productId: productsByCategory[categoryId] || [],
      }));

      // Prepare request payload
      const payload = {
        image: imageUrl,
        name: storeName,
        code: storeCode,
        flavorId: flavorIds,
        toppingId: toppingIds,
        categories: categoriesData,
        address: {
          fullAddress,
          street,
          city,
          state,
          country,
          zipCode,
          latitude: 0,
          longitude: 0,
        },
        region: "",
        latitude: 0,
        longitude: 0,
        ...weekdayData,
      };

      if (isEditMode && storeId) {
        // Update store
        const response = await updateStore({ id: storeId, ...payload }).unwrap();
        showToast(response.message || "Store updated successfully");
      } else {
        // Create store
        const response = await createStore(payload).unwrap();
        showToast(response.message || "Store created successfully");
      }
      navigate("/manage-stores");
    } catch (error: any) {
      const errorMessage = error?.data?.message || "Failed to create store. Please try again.";
      showError(errorMessage);
    }
  };

  // Handle image upload
  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = (event.target as HTMLInputElement).files;
    if (files && files[0]?.type.includes("image")) {
      setImageFile(files[0]);
      setImage(URL.createObjectURL(files[0]));
      if (errors.image) {
        clearError("image");
      }
    } else {
      showError("Please add image only.");
    }
  };

  return (
    <>
      <Loader isLoad={isLoading || isLoadingStoreDetail} />
      <div className="main_title">
        <h1>{isEditMode ? "Edit Store" : "Add Store"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-stores")}>
            Store Management
          </button>{" "}
          - {isEditMode ? "Edit Store" : "Add Store"}
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="General Store Info" {...a11yProps(0)} />
              <Tab label="Store Availability" {...a11yProps(1)} />
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        <form className="form" action="">
          <CustomTabPanel value={valueTabs} index={0}>
            <div className="gap_p">
              {/* Store Image */}
              <div className="control_group w_100">
                <label htmlFor="store_image">Store Image</label>
                <div className="gap_p">
                  <div className="control_group w_33">
                    {image ? (
                      <div className="upload_image no_radius v2">
                        <div className="upload_image_holder">
                          <figure>
                            <CardMedia
                              component="img"
                              image={image}
                              alt="Uploaded store photo"
                            />
                          </figure>
                          <IconButton
                            aria-label="Remove uploaded image"
                            onClick={() => {
                              setImage("");
                              setImageFile(null);
                              setUploadedImageUrl("");
                              setFileName("");
                            }}
                            size="small"
                          >
                            <CloseIcon />
                          </IconButton>
                        </div>
                      </div>
                    ) : (
                      <label
                        className="upload_image no_radius v2"
                        htmlFor="store_image"
                      >
                        <Input
                          sx={{ display: "none" }}
                          id="store_image"
                          type="file"
                          inputProps={{ accept: "image/png,image/jpeg" }}
                          onChange={handleImageChange}
                        />
                        <span className="upload_image_holder">
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt="placeholder"
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="Upload"
                              />
                            </span>
                          </figure>
                        </span>
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Store Name */}
              <div className="control_group w_66">
                <label htmlFor="store_name">Store Name *</label>
                <TextField
                  id="store_name"
                  placeholder="Enter Store Name"
                  fullWidth
                  hiddenLabel
                  value={storeName}
                  onChange={(e) => {
                    setStoreName(e.target.value);
                    if (errors.storeName) {
                      clearError("storeName");
                    }
                  }}
                  error={!!errors.storeName}
                  helperText={errors.storeName}
                />
              </div>

              {/* Store Code */}
              <div className="control_group w_33">
                <label htmlFor="store_code">Store Code (Internal Reference) *</label>
                <TextField
                  id="store_code"
                  placeholder="Enter Store Code"
                  fullWidth
                  hiddenLabel
                  value={storeCode}
                  onChange={(e) => {
                    setStoreCode(e.target.value);
                    if (errors.storeCode) {
                      clearError("storeCode");
                    }
                  }}
                  error={!!errors.storeCode}
                  helperText={errors.storeCode}
                />
              </div>

              {/* Flavour Dropdown */}
              <div className="control_group w_50">
                <label htmlFor="flavour">Select Flavors</label>
                <Select
                  multiple
                  id="flavour"
                  fullWidth
                  displayEmpty
                  value={flavorIds}
                  onChange={(e) => setFlavorIds(e.target.value as string[])}
                  renderValue={(selected) => {
                    if (selected.length === 0) return "Choose Flavour";
                    return selected
                      .map((id) => flavorList.find((f) => f.id === id)?.name)
                      .filter(Boolean)
                      .join(", ");
                  }}
                >
                  {flavorList.map((flavor) => (
                    <MenuItem key={flavor.id} value={flavor.id}>
                      {flavor.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {/* Topping Dropdown */}
              <div className="control_group w_50">
                <label htmlFor="topping_category">Select Toppings</label>
                <Select
                  multiple
                  id="topping_category"
                  fullWidth
                  displayEmpty
                  value={toppingIds}
                  onChange={(e) => setToppingIds(e.target.value as string[])}
                  renderValue={(selected) => {
                    if (selected.length === 0) return "Choose Toppings";
                    return selected
                      .map((id) => toppingList.find((t) => t.id === id)?.name)
                      .filter(Boolean)
                      .join(", ");
                  }}
                >
                  {toppingList.map((topping) => (
                    <MenuItem key={topping.id} value={topping.id}>
                      {topping.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              <div className="control_group w_50">
                <label>Select Product Category</label>
                <Select
                  multiple
                  fullWidth
                  displayEmpty
                  value={productCategories}
                  onChange={(e) => {
                    const selected = e.target.value as string[];
                    setProductCategories(selected);

                    // Initialize empty product lists for new selections
                    selected.forEach((cat) => {
                      if (!productsByCategory[cat]) {
                        setProductsByCategory((prev) => ({
                          ...prev,
                          [cat]: [],
                        }));
                      }
                    });
                  }}
                  renderValue={(selected) => {
                    if (selected.length === 0) return "Choose Product Category";
                    return selected
                      .map((id) => categoryList.find((c) => c.id === id)?.name)
                      .filter(Boolean)
                      .join(", ");
                  }}
                >
                  {categoryList.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {productCategories.map((categoryId) => {
                const category = categoryList.find((c) => c.id === categoryId);
                const products = getProductsForCategory(categoryId);
                return (
                  <div key={categoryId} className="control_group w_50">
                    <label>{category?.name || "Category"} Products</label>
                    <Select
                      multiple
                      fullWidth
                      value={productsByCategory[categoryId] || []}
                      onChange={(e) => {
                        const selectedProducts = e.target.value as string[];
                        setProductsByCategory((prev) => ({
                          ...prev,
                          [categoryId]: selectedProducts,
                        }));
                      }}
                      renderValue={(selected) => {
                        if (selected.length === 0) return `Select ${category?.name || "Category"} Products`;
                        return selected
                          .map((id) => products.find((p) => p.id === id)?.name)
                          .filter(Boolean)
                          .join(", ");
                      }}
                    >
                      {products.map((product) => (
                        <MenuItem key={product.id} value={product.id}>
                          {product.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                );
              })}

              {/* Full Address */}
              <div className="control_group w_66">
                <label htmlFor="full_address">Full Address *</label>
                <TextField
                  id="full_address"
                  placeholder="Enter Full Address"
                  fullWidth
                  hiddenLabel
                  value={fullAddress}
                  onChange={(e) => {
                    setFullAddress(e.target.value);
                    if (errors.fullAddress) {
                      clearError("fullAddress");
                    }
                  }}
                  error={!!errors.fullAddress}
                  helperText={errors.fullAddress}
                />
              </div>

              {/* Street */}
              <div className="control_group w_33">
                <label htmlFor="street">Street *</label>
                <TextField
                  id="street"
                  placeholder="Enter Street"
                  fullWidth
                  hiddenLabel
                  value={street}
                  onChange={(e) => {
                    setStreet(e.target.value);
                    if (errors.street) {
                      clearError("street");
                    }
                  }}
                  error={!!errors.street}
                  helperText={errors.street}
                />
              </div>

              {/* City */}
              <div className="control_group w_33">
                <label htmlFor="city">City *</label>
                <TextField
                  id="city"
                  placeholder="Enter City"
                  fullWidth
                  hiddenLabel
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    if (errors.city) {
                      clearError("city");
                    }
                  }}
                  error={!!errors.city}
                  helperText={errors.city}
                />
              </div>

              {/* State */}
              <div className="control_group w_33">
                <label htmlFor="state">State *</label>
                <TextField
                  id="state"
                  placeholder="Enter State"
                  fullWidth
                  hiddenLabel
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value);
                    if (errors.state) {
                      clearError("state");
                    }
                  }}
                  error={!!errors.state}
                  helperText={errors.state}
                />
              </div>

              {/* Country - Input Field */}
              <div className="control_group w_33">
                <label htmlFor="country">Country *</label>
                <TextField
                  id="country"
                  placeholder="Enter Country"
                  fullWidth
                  hiddenLabel
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                    if (errors.country) {
                      clearError("country");
                    }
                  }}
                  error={!!errors.country}
                  helperText={errors.country}
                />
              </div>

              {/* Zip Code */}
              <div className="control_group w_33">
                <label htmlFor="zip_code">Zip Code *</label>
                <TextField
                  id="zip_code"
                  placeholder="Enter Zip Code"
                  fullWidth
                  hiddenLabel
                  value={zipCode}
                  onChange={(e) => {
                    setZipCode(e.target.value);
                    if (errors.zipCode) {
                      clearError("zipCode");
                    }
                  }}
                  error={!!errors.zipCode}
                  helperText={errors.zipCode}
                />
              </div>

              {/* Map */}
              <div className="control_group w_100">
                <label htmlFor="map">Map Location</label>
                <iframe
                  title="map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3559.9533272900557!2d80.98112951501063!3d26.84669338315635!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399be2a68c3f2a0f%3A0x9b63c82ac1f3a60!2sLucknow%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1616580979832!5m2!1sen!2sin"
                  width="100%"
                  height="250"
                  style={{
                    border: 0,
                    borderRadius: "10px",
                    display: "flex",
                    overflow: "hidden",
                  }}
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>

              <div className="form_btn">
                <Button
                  className="btn btn_primary"
                  onClick={() => setValueTabs(1)}
                >
                  Continue
                </Button>
              </div>
            </div>
          </CustomTabPanel>

          {/* Availability TAB */}
          <CustomTabPanel value={valueTabs} index={1}>
            <div className="section_title">
              <h2>Weekly Availability</h2>
            </div>
            {(Object.keys(availability) as DayOfWeek[]).map((day) => (
              <div className="availability_item" key={day}>
                <div className="gap_p aic">
                  <div className="control_group w_33">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={availability[day].enabled}
                          onChange={() => toggleAvailability(day)}
                        />
                      }
                      label={day}
                    />
                  </div>
                  {availability[day].enabled && (
                    <div className="control_group w_66">
                      <div className="gap_p">
                        <div className="control_group flex w_50">
                          <label htmlFor="startTime">Start Time</label>
                          <TextField
                            type="time"
                            id="startTime"
                            value={availability[day].start}
                            onChange={(e) =>
                              updateTime(day, "start", e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                        </div>
                        <div className="control_group flex w_50">
                          <label htmlFor="endTime">End Time</label>
                          <TextField
                            type="time"
                            id="endTime"
                            value={availability[day].end}
                            onChange={(e) =>
                              updateTime(day, "end", e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div className="form_btn">
              <Button
                className="btn btn_secondary"
                onClick={() => setValueTabs(0)}
              >
                Back
              </Button>
              <Button
                className="btn btn_primary"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                Submit
              </Button>
            </div>
          </CustomTabPanel>
        </form>
      </div>
    </>
  );
};

export default AddStore;
